// frontend/src/pages/Marketplace.js
// Poshmark / Mercari listing registry + SKU mapping (2026-09-30).
// Spec: docs/euphoric-sync-extension-spec.md. Rows come from marketplace_listings,
// which holds one row per site listing — including listings not yet mapped to a
// SKU, and several listings for one SKU (duplicates, an oversell risk in their own
// right, flagged here).
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api, channelLabel } from '../App';

function fmtTime(iso) {
  if (!iso) return 'never';
  const d = new Date(iso.endsWith('Z') || iso.includes('T') ? iso : iso + 'Z');
  return d.toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' });
}
const STATUS_COLORS = { active: '#22c55e', delisted: 'var(--muted)', sold: 'var(--accent2)', error: 'var(--danger)', gone: 'var(--muted)' };
const MAP_LABELS = { exact: 'auto · exact SKU', sibling: 'auto · sibling SKU', manual: 'mapped by you' };

export default function Marketplace() {
  const [data, setData]     = useState(null);
  const [channel, setChannel] = useState('all');
  const [view, setView]     = useState('needs');      // needs | active | dupes | all
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);       // listing_id
  const [skuQuery, setSkuQuery] = useState('');
  const [skuHits, setSkuHits] = useState([]);
  const [busy, setBusy]     = useState(false);
  const [msg, setMsg]       = useState(null);

  const load = useCallback(() => api.get('/marketplace/listings', { params: { channel } }).then(r => setData(r.data)), [channel]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!editing || skuQuery.trim().length < 2) { setSkuHits([]); return; }
    const t = setTimeout(() => api.get('/marketplace/sku-search', { params: { q: skuQuery.trim() } }).then(r => setSkuHits(r.data)), 250);
    return () => clearTimeout(t);
  }, [skuQuery, editing]);

  async function saveMap(row, sku) {
    setBusy(true);
    try {
      await api.patch(`/marketplace/listings/${row.channel}/${encodeURIComponent(row.listing_id)}`, { sku });
      setEditing(null); setSkuQuery(''); setSkuHits([]);
      await load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  }
  async function setSetting(key, value, note) {
    setBusy(true);
    try { await api.post('/marketplace/settings', { [key]: value }); setMsg(note); await load(); }
    finally { setBusy(false); }
  }

  if (!data) return <div className="loading">Loading marketplace listings…</div>;
  const s = data.summary || {};
  const rows = (data.rows || []).filter(r => {
    if (view === 'needs' && r.sku) return false;
    if (view === 'active' && r.status !== 'active') return false;
    if (view === 'dupes' && !(r.dup_count > 1)) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!`${r.title || ''} ${r.sku || ''} ${r.channel_sku || ''} ${r.listing_id}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div>
      <div className="page-header">
        <div className="page-title">🔗 Poshmark / Mercari</div>
        <button className="btn btn-ghost" onClick={load} disabled={busy}>🔄 Refresh</button>
      </div>

      <div className="dash-kpi-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card"><div className="stat-value">{s.total ?? 0}</div><div className="stat-label">Listings known</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: s.unmapped > 0 ? 'var(--warn)' : undefined }}>{s.unmapped ?? 0}</div><div className="stat-label">Need a SKU</div></div>
        <div className="stat-card"><div className="stat-value">{s.active ?? 0}</div><div className="stat-label">Active on site</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: s.errors > 0 ? 'var(--danger)' : undefined }}>{s.errors ?? 0}</div><div className="stat-label">Delist errors</div></div>
      </div>

      {/* Safety switches — both legs ship in dry run; nothing is clicked on a live site until these are off. */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
          <span><b>Delist:</b>{' '}
            <span style={{ color: data.dryRun.delist ? 'var(--warn)' : 'var(--accent2)', fontWeight: 600 }}>{data.dryRun.delist ? 'DRY RUN — logs only' : 'LIVE'}</span>
            <button className="btn btn-ghost" style={{ marginLeft: 8, fontSize: 11, padding: '2px 8px' }} disabled={busy}
              onClick={() => setSetting('crosslist_delist_dryrun', data.dryRun.delist ? '0' : '1', data.dryRun.delist ? 'Delisting is now LIVE — the extension will take listings down.' : 'Delisting back to dry run.')}>
              {data.dryRun.delist ? 'Go live' : 'Back to dry run'}
            </button>
          </span>
          <span><b>Sales import:</b>{' '}
            <span style={{ color: data.dryRun.sales ? 'var(--warn)' : 'var(--accent2)', fontWeight: 600 }}>{data.dryRun.sales ? 'DRY RUN — logs only' : 'LIVE'}</span>
            <button className="btn btn-ghost" style={{ marginLeft: 8, fontSize: 11, padding: '2px 8px' }} disabled={busy}
              onClick={() => setSetting('crosslist_sales_dryrun', data.dryRun.sales ? '0' : '1', data.dryRun.sales ? 'Sales import is now LIVE.' : 'Sales import back to dry run.')}>
              {data.dryRun.sales ? 'Go live' : 'Back to dry run'}
            </button>
          </span>
          <span style={{ color: 'var(--muted)' }}>
            Sales watermark (Poshmark): <b>{data.salesSince?.poshmark ? fmtTime(data.salesSince.poshmark) : 'not set'}</b>
            <button className="btn btn-ghost" style={{ marginLeft: 8, fontSize: 11, padding: '2px 8px' }} disabled={busy}
              title="Sales older than this are ignored — the 40 Poshmark sales already entered by hand must not import again and decrement stock twice."
              onClick={() => setSetting('poshmark_sales_since', new Date().toISOString(), 'Watermark set to now — only sales after this import.')}>Set to now</button>
          </span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
          Extension last seen — Poshmark {fmtTime(data.lastSeen?.poshmark)} · Mercari {fmtTime(data.lastSeen?.mercari)}.
          Last import — Poshmark {fmtTime(data.lastImport?.poshmark)} · Mercari {fmtTime(data.lastImport?.mercari)}.
        </div>
        {msg && <div style={{ marginTop: 8, fontSize: 12 }}>{msg} <button className="btn btn-ghost" style={{ fontSize: 11, padding: '1px 6px' }} onClick={() => setMsg(null)}>✕</button></div>}
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {[['needs', 'Needs a SKU'], ['active', 'Active'], ['dupes', 'Duplicates'], ['all', 'All']].map(([k, l]) => (
            <button key={k} className={`inv-filter-btn ${view === k ? 'active' : ''}`} onClick={() => setView(k)}>{l}</button>
          ))}
          <select value={channel} onChange={e => setChannel(e.target.value)} style={{ marginLeft: 12 }}>
            <option value="all">Both channels</option><option value="poshmark">Poshmark</option><option value="mercari">Mercari</option>
          </select>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Title, SKU or listing id…"
            style={{ flex: 1, minWidth: 200, marginLeft: 'auto', maxWidth: 320 }} />
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="empty-state"><div className="empty-icon">—</div>
          {s.total ? 'Nothing matches this view.' : 'No listings imported yet — run the extension’s import once it is loaded on the Bot Machine.'}
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Channel</th><th>Listing</th><th>Site SKU</th><th>Sync SKU</th>
                <th style={{ textAlign: 'right' }}>Price</th><th style={{ textAlign: 'right' }}>Master qty</th><th>Status</th><th></th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={`${r.channel}-${r.listing_id}`} style={r.status === 'error' ? { background: 'rgba(255,77,109,.06)' } : undefined}>
                    <td style={{ fontSize: 12 }}>{channelLabel(r.channel)}</td>
                    <td style={{ maxWidth: 300 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.url ? <a href={r.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent2)' }}>{r.title || r.listing_id}</a> : (r.title || r.listing_id)}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }} className="mono">{r.listing_id}
                        {!r.active_item && <span title="Poshmark shows this listing as inactive — hidden from browse, but still buyable by URL" style={{ marginLeft: 6, color: 'var(--warn)' }}>· inactive on site</span>}
                        {r.dup_count > 1 && <span title={`${r.dup_count} active listings share this SKU — all of them are delisted together`} style={{ marginLeft: 6, color: 'var(--danger)', fontWeight: 600 }}>· duplicate ×{r.dup_count}</span>}
                      </div>
                    </td>
                    <td className="mono" style={{ fontSize: 11 }}>{r.channel_sku || '—'}</td>
                    <td style={{ minWidth: 220 }}>
                      {editing === r.listing_id ? (
                        <div>
                          <input autoFocus value={skuQuery} onChange={e => setSkuQuery(e.target.value)} placeholder="Type a SKU or title…" style={{ width: '100%' }} />
                          <div style={{ maxHeight: 160, overflowY: 'auto', marginTop: 4 }}>
                            {skuHits.map(h => (
                              <div key={h.sku} onClick={() => saveMap(r, h.sku)}
                                style={{ padding: '3px 6px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid var(--border)' }}>
                                <span className="mono" style={{ color: 'var(--accent2)' }}>{h.sku}</span> · qty {h.quantity} · {(h.title || '').slice(0, 40)}
                              </div>
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                            {r.sku && <button className="btn btn-ghost" style={{ fontSize: 11 }} disabled={busy} onClick={() => saveMap(r, null)}>Unmap</button>}
                            <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => { setEditing(null); setSkuQuery(''); }}>Cancel</button>
                          </div>
                        </div>
                      ) : r.sku ? (
                        <div>
                          <Link to={`/inventory?search=${encodeURIComponent(r.sku)}`} className="mono" style={{ color: 'var(--accent2)', textDecoration: 'none' }}>{r.sku}</Link>
                          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{MAP_LABELS[r.map_source] || r.map_source || ''}</div>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--warn)', fontSize: 12 }} title={r.map_note || ''}>{r.map_note ? '⚠ ' + r.map_note.slice(0, 40) : 'not mapped'}</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{r.price != null ? `$${Number(r.price).toFixed(2)}` : '—'}</td>
                    <td style={{ textAlign: 'right', color: r.sku && r.master_qty <= 0 ? 'var(--danger)' : undefined }}>{r.sku ? r.master_qty : '—'}</td>
                    <td>
                      <span className="badge" style={{ background: 'var(--surface2)', color: STATUS_COLORS[r.status] || 'var(--muted)' }}>{r.status}</span>
                      {r.last_error && <div style={{ fontSize: 11, color: r.status === 'error' ? 'var(--danger)' : 'var(--muted)', maxWidth: 240, whiteSpace: 'normal' }}>{r.last_error.slice(0, 120)}</div>}
                    </td>
                    <td><button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => { setEditing(r.listing_id); setSkuQuery(r.channel_sku || r.title || ''); }}>{r.sku ? 'Change' : 'Map'}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
